'use client';

import React from 'react';
import { useGsapReveal } from '@/hooks/useGsapReveal';
import { trackEvent } from '@/lib/analytics';

type Status = 'yes' | 'partial' | 'no';

const COMPARISON_ROWS: { label: string; intel: Status; traditional: Status; lms: Status }[] = [
  { label: 'Curriculum graph mapping', intel: 'yes', traditional: 'no', lms: 'partial' },
  { label: 'Spatial precision feedback', intel: 'yes', traditional: 'no', lms: 'no' },
  { label: 'Concept-mapped assessment', intel: 'yes', traditional: 'no', lms: 'partial' },
  { label: 'Formula-level diagnostics', intel: 'yes', traditional: 'no', lms: 'partial' },
  { label: 'Parent intelligence reports', intel: 'yes', traditional: 'partial', lms: 'partial' },
  { label: 'Ghost corrections + feedback', intel: 'yes', traditional: 'no', lms: 'partial' },
  { label: 'Concept gap detection', intel: 'yes', traditional: 'partial', lms: 'partial' },
  { label: 'Dynamic intervention cohorts', intel: 'yes', traditional: 'no', lms: 'partial' },
];

const STATUS_LABEL: Record<Status, string> = {
  yes: 'Included',
  partial: 'Partial',
  no: 'Not included',
};

// Hand-drawn checkmark - casual two-stroke style
const HandDrawnCheck: React.FC<{ isBlue?: boolean }> = ({ isBlue }) => (
  <svg viewBox="0 0 32 32" className="sketch-icon" fill="none">
    <path
      d="M6 17c1.5 1.8 3.2 4.1 5.5 6.5 0.8 0.8 1.2 0.6 1.8-0.2C16 19.5 20 13 26 7"
      stroke={isBlue ? '#2b7bff' : '#329757'}
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        strokeDasharray: '60',
        strokeDashoffset: '0',
      }}
    />
  </svg>
);

// Hand-drawn X mark - loose crossed strokes
const HandDrawnX: React.FC = () => (
  <svg viewBox="0 0 32 32" className="sketch-icon" fill="none">
    <path
      d="M8 8c2 2.5 4.5 5 8 8.5 3.5 3.5 6 5.5 8 7.5"
      stroke="#d94848"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <path
      d="M24 9c-2.5 2-5.5 4.5-8 7.5-3 3.5-5.5 6-8 8"
      stroke="#d94848"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

// Hand-drawn dash/partial - wavy horizontal line
const HandDrawnDash: React.FC = () => (
  <svg viewBox="0 0 32 32" className="sketch-icon" fill="none">
    <path
      d="M6 16c3-1 6 1.5 10 0.5s7-2 10 0"
      stroke="#7a8599"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

const StatusIcon: React.FC<{ status: Status; isIntel?: boolean }> = ({ status, isIntel }) => {
  if (status === 'yes') {
    return <HandDrawnCheck isBlue={isIntel} />;
  }
  if (status === 'partial') {
    return <HandDrawnDash />;
  }
  return <HandDrawnX />;
};

// Generate a wobbly path for borders
const generateWobblyLine = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  wobble: number = 2
): string => {
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  const ctrl1X = x1 + (midX - x1) * 0.5 + (Math.random() - 0.5) * wobble;
  const ctrl1Y = y1 + (midY - y1) * 0.5 + (Math.random() - 0.5) * wobble;
  const ctrl2X = midX + (x2 - midX) * 0.5 + (Math.random() - 0.5) * wobble;
  const ctrl2Y = midY + (y2 - midY) * 0.5 + (Math.random() - 0.5) * wobble;
  return `M${x1},${y1} Q${ctrl1X},${ctrl1Y} ${midX},${midY} Q${ctrl2X},${ctrl2Y} ${x2},${y2}`;
};

// Wobbly horizontal line component
const WobblyHLine: React.FC<{ seed?: number }> = ({ seed = 0 }) => {
  // Use seed to create consistent but varied wobble
  const wobbleAmount = 1.5;
  const path = `M0,1 Q25,${1 + (seed % 3) - 1} 50,${1 + ((seed * 2) % 3) - 1} Q75,${1 + ((seed * 3) % 3) - 1} 100,1`;

  return (
    <svg
      className="sketch-hline"
      viewBox="0 0 100 3"
      preserveAspectRatio="none"
    >
      <path
        d={path}
        stroke="#3a4a5e"
        strokeWidth="0.8"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
};

const Comparison: React.FC = () => {
  const sectionRef = useGsapReveal<HTMLElement>({ y: 30, duration: 0.8 });

  return (
    <section id="comparison" ref={sectionRef} className="sketch-comparison scroll-mt-32">
      <div className="sketch-comparison__inner">
        <div className="section-header section-header--secondary section-header--comparison">
          <span className="section-eyebrow">Why Intelgrader</span>
          <h2 className="section-title">Beyond grading. Into learning intelligence.</h2>
          <p className="section-subtitle">See which concepts slip, which formulas break, and what to fix next.</p>
        </div>

        {/* Whiteboard container */}
        <div className="sketch-board">
          {/* Grid pattern overlay */}
          <div className="sketch-board__grid" />

          {/* The table */}
          <div className="sketch-table">
            {/* Header row */}
            <div className="sketch-table__row sketch-table__row--head">
              <div className="sketch-table__cell sketch-table__cell--label">
                <span className="sketch-header-text">Feature</span>
              </div>
              <div className="sketch-table__cell sketch-table__cell--intel">
                <span className="sketch-header-text sketch-header-text--intel">Intelgrader</span>
              </div>
              <div className="sketch-table__cell">
                <span className="sketch-header-text">Traditional</span>
              </div>
              <div className="sketch-table__cell">
                <span className="sketch-header-text">Generic LMS</span>
              </div>
            </div>

            <WobblyHLine seed={0} />

            {/* Data rows */}
            {COMPARISON_ROWS.map((row, idx) => (
              <React.Fragment key={row.label}>
                <div className="sketch-table__row">
                  <div className="sketch-table__cell sketch-table__cell--label">
                    <span className="sketch-label-text">{row.label}</span>
                  </div>
                  <div className="sketch-table__cell sketch-table__cell--intel" data-label="Intelgrader">
                    <StatusIcon status={row.intel} isIntel />
                    <span className="sr-only">{STATUS_LABEL[row.intel]}</span>
                  </div>
                  <div className="sketch-table__cell" data-label="Traditional">
                    <StatusIcon status={row.traditional} />
                    <span className="sr-only">{STATUS_LABEL[row.traditional]}</span>
                  </div>
                  <div className="sketch-table__cell" data-label="Generic LMS">
                    <StatusIcon status={row.lms} />
                    <span className="sr-only">{STATUS_LABEL[row.lms]}</span>
                  </div>
                </div>
                {idx < COMPARISON_ROWS.length - 1 && <WobblyHLine seed={idx + 1} />}
              </React.Fragment>
            ))}
          </div>

          {/* Decorative corner scribbles */}
          <svg className="sketch-corner sketch-corner--tl" viewBox="0 0 20 20" fill="none">
            <path d="M2,2 Q5,3 8,2 Q11,1 14,3" stroke="#3a4a5e" strokeWidth="1.2" strokeLinecap="round" />
            <path d="M2,2 Q3,5 2,8 Q1,11 3,14" stroke="#3a4a5e" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          <svg className="sketch-corner sketch-corner--tr" viewBox="0 0 20 20" fill="none">
            <path d="M18,2 Q15,3 12,2 Q9,1 6,3" stroke="#3a4a5e" strokeWidth="1.2" strokeLinecap="round" />
            <path d="M18,2 Q17,5 18,8 Q19,11 17,14" stroke="#3a4a5e" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          <svg className="sketch-corner sketch-corner--bl" viewBox="0 0 20 20" fill="none">
            <path d="M2,18 Q5,17 8,18 Q11,19 14,17" stroke="#3a4a5e" strokeWidth="1.2" strokeLinecap="round" />
            <path d="M2,18 Q3,15 2,12 Q1,9 3,6" stroke="#3a4a5e" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          <svg className="sketch-corner sketch-corner--br" viewBox="0 0 20 20" fill="none">
            <path d="M18,18 Q15,17 12,18 Q9,19 6,17" stroke="#3a4a5e" strokeWidth="1.2" strokeLinecap="round" />
            <path d="M18,18 Q17,15 18,12 Q19,9 17,6" stroke="#3a4a5e" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
        </div>

        {/* Intermediate CTA */}
        <div className="text-center mt-16">
          <a
            href="/book-demo"
            onClick={() => trackEvent('book_demo_click', 'CTA', 'comparison')}
            className="btn btn-primary btn-lg"
          >
            Book a Demo →
          </a>
        </div>
      </div>
    </section>
  );
};

export default Comparison;
